import React, { useEffect, useRef, useState } from "react";

const apiBaseUrl = process.env.REACT_APP_API_URL;

const AddressAutocomplete = ({
  value,
  onChange,
  uniqueCode,
  suggestionsUrl,
  disabled,
  placeholder,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [searched, setSearched] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef("");
  const justSelectedRef = useRef(false);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    const q = (value || "").trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      setSearched(false);
      return;
    }
    if (q === lastQueryRef.current) {
      setOpen(focused && suggestions.length > 0);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      lastQueryRef.current = q;
      setLoading(true);
      setOpen(focused);
      try {
        const url = suggestionsUrl
          ? `${apiBaseUrl}${suggestionsUrl}?q=${encodeURIComponent(q)}`
          : `${apiBaseUrl}/api/event-participations/${uniqueCode}/address-suggestions?q=${encodeURIComponent(q)}`;
        const resp = await fetch(url);
        if (!resp.ok) {
          setSuggestions([]);
          return;
        }
        const body = await resp.json();
        setSuggestions(body.suggestions || []);
        setOpen(focused);
        setSearched(true);
        setHighlight(-1);
      } catch {
        setSuggestions([]);
        setSearched(true);
      } finally {
        setLoading(false);
      }
    }, 2000);

    return () => clearTimeout(debounceRef.current);
  }, [focused, suggestions.length, suggestionsUrl, uniqueCode, value]);

  const pick = (s) => {
    justSelectedRef.current = true;
    lastQueryRef.current = s.label;
    onChange(s.label);
    setSuggestions([]);
    setOpen(false);
    setHighlight(-1);
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className="input input-bordered w-full"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          lastQueryRef.current = "";
          justSelectedRef.current = false;
          onChange(e.target.value);
        }}
        onFocus={() => {
          setFocused(true);
          if ((value || "").trim().length >= 3) setOpen(true);
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={handleKey}
        disabled={disabled}
        autoComplete="off"
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
          …
        </div>
      )}
      {open && (loading || searched || suggestions.length > 0) && (
        <div className="absolute z-[9999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-xl max-h-64 overflow-auto">
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100">
            Lob address suggestions
          </div>
          {loading && suggestions.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">
              Searching addresses…
            </div>
          ) : null}
          {!loading && searched && suggestions.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">
              No matches yet. Keep typing a street address.
            </div>
          ) : null}
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={`${s.label}-${i}`}
              className={`block w-full text-left px-3 py-2 text-sm cursor-pointer ${
                i === highlight ? "bg-gray-100" : "hover:bg-gray-50"
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              <div className="font-medium">{s.primary}</div>
              <div className="text-xs text-gray-500">
                {[s.city, s.state, s.zip].filter(Boolean).join(", ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
